import React, { useMemo, useState } from 'react';
import { Box, Paper, Typography, Button, TextField, CircularProgress } from '@mui/material';
import DataTable from 'react-data-table-component';
import { downloadPreleadsExcel, getPreleadsResumen } from '../services/ventaDirectaReportApi';

const VentaDirectaPreleadsReportPage = () => {
  const [fecha, setFecha] = useState('');
  const [loadingResumen, setLoadingResumen] = useState(false);
  const [loadingExcel, setLoadingExcel] = useState(false);
  const [error, setError] = useState('');
  const [resumen, setResumen] = useState([]);

  const isFechaValida = Boolean(fecha) && /^\d{4}-\d{2}-\d{2}$/.test(fecha);

  const mapAxiosErrorToMessage = (err) => {
    const status = err?.response?.status;
    if (status === 400) return 'Fecha inválida, use YYYY-MM-DD';
    if (status === 401 || status === 403) return 'No autorizado / sesión expirada';
    if (status === 404) return 'Endpoint no encontrado (404). Verifique la URL del backend.';
    return 'Error al generar reporte';
  };

  const buildDebugError = (err) => {
    const status = err?.response?.status;
    const statusText = err?.response?.statusText;
    const data = err?.response?.data;

    // Si viene blob (por ejemplo en excel), intentamos mostrar algo útil
    const dataHint =
      typeof data === 'string'
        ? data
        : data && typeof data === 'object'
          ? JSON.stringify(data)
          : '';

    return [status ? `HTTP ${status}` : null, statusText || null, dataHint || null]
      .filter(Boolean)
      .join(' - ');
  };

  const handleVerResumen = async () => {
    if (!isFechaValida) {
      setError('Debe seleccionar una fecha válida (YYYY-MM-DD)');
      return;
    }

    setError('');
    setLoadingResumen(true);
    try {
      const response = await getPreleadsResumen(fecha);
      const data = response?.data;
      const rows = Array.isArray(data) ? data : (data?.resumen || data?.items || []);
      setResumen(Array.isArray(rows) ? rows : []);
    } catch (err) {
      // Diagnóstico en consola
      // eslint-disable-next-line no-console
      console.error('[VentaDirectaPreleadsReportPage] Error resumen', {
        url: '/api/venta-directa/reportes/preleads/resumen',
        fecha,
        apiBaseUrl: window.API_BASE_URL,
        status: err?.response?.status,
        data: err?.response?.data,
        message: err?.message,
      });

      const debug = buildDebugError(err);
      setError(`${mapAxiosErrorToMessage(err)}${debug ? ` (${debug})` : ''}`);
      setResumen([]);
    } finally {
      setLoadingResumen(false);
    }
  };

  const handleDescargarExcel = async () => {
    if (!isFechaValida) {
      setError('Debe seleccionar una fecha válida (YYYY-MM-DD)');
      return;
    }

    setError('');
    setLoadingExcel(true);
    try {
      await downloadPreleadsExcel(fecha);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[VentaDirectaPreleadsReportPage] Error excel', {
        url: '/api/venta-directa/reportes/preleads/excel',
        fecha,
        apiBaseUrl: window.API_BASE_URL,
        status: err?.response?.status,
        data: err?.response?.data,
        message: err?.message,
      });

      const debug = buildDebugError(err);
      setError(`${mapAxiosErrorToMessage(err)}${debug ? ` (${debug})` : ''}`);
    } finally {
      setLoadingExcel(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        name: 'NombrePlantilla',
        selector: (row) => row?.NombrePlantilla ?? row?.nombrePlantilla ?? row?.templateName ?? '-',
        sortable: true,
        grow: 2,
      },
      {
        name: 'Total',
        selector: (row) => row?.Total ?? row?.total ?? 0,
        sortable: true,
        right: true,
        maxWidth: '120px',
      },
    ],
    []
  );

  return (
    <Box className="apple-container">
      <Paper className="apple-card" sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Reporte PreLeads (Venta Directa)
        </Typography>

        {error ? (
          <Box sx={{ mb: 2 }}>
            <Typography color="error" variant="body2">
              {error}
            </Typography>
          </Box>
        ) : null}

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
          <TextField
            label="Fecha"
            type="date"
            value={fecha}
            onChange={(e) => setFecha(e.target.value)}
            // InputLabelProps está deprecado en MUI v6; lo dejamos fuera.
            size="small"
          />

          <Button
            variant="contained"
            onClick={handleVerResumen}
            disabled={!isFechaValida || loadingResumen}
          >
            {loadingResumen ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} />
                <span>Cargando...</span>
              </Box>
            ) : (
              'Ver resumen'
            )}
          </Button>

          <Button
            variant="outlined"
            onClick={handleDescargarExcel}
            disabled={!isFechaValida || loadingExcel}
          >
            {loadingExcel ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} />
                <span>Descargando...</span>
              </Box>
            ) : (
              'Descargar Excel'
            )}
          </Button>
        </Box>

        <DataTable
          columns={columns}
          data={resumen}
          pagination
          paginationPerPage={10}
          paginationRowsPerPageOptions={[10, 20, 30]}
          persistTableHead
          noDataComponent={loadingResumen ? 'Cargando...' : 'Sin datos'}
        />
      </Paper>
    </Box>
  );
};

export default VentaDirectaPreleadsReportPage;
